import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import type { InputRef } from "antd";
import { Button, DatePicker, Drawer, Form, Image, Input, Radio, Select, Table, message, Upload } from "antd";
import type { FilterConfirmProps } from "antd/es/table/interface";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

import { Colors } from "../../../constants/Colors";
import DrawerFooter from "../../../components/layout/drawer/DrawerFooter";
import { data as MapData } from "../../../data/data";
import requiredRule from "../../../utils/formRules/requiredRule";
import GeneralModal from "../../../utils/GeneralModal";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import fileToBase64 from "../utils/fileToBase64";
import { createUploadProps } from "../utils/uploadProps";
import { createFaceCallVideosColumns } from "./content/faceCallColumns";
import { ActionType, FilterType, MethodType, VideoDataIndex, VideoOptionsType, VideoType } from "./content/faceCallTypes";
import { faceCallVideoDelete, faceCallVideoMutation } from "./services/faceCall.services";
import "./videosTabContent.scss";

const { faceCallVideosQueryTypes } = MapData;

interface VideosTabContentProps {
  videosData: VideoType[];
  videoOptions: VideoOptionsType | undefined,
  selectPerformers: string[] | null,
  setSelectPerformers: Function,
  isAdmin: boolean,
  refreshData: () => void,
}

const VideosTabContent = ({
  videosData,
  videoOptions,
  selectPerformers,
  setSelectPerformers,
  isAdmin,
  refreshData
}: VideosTabContentProps) => {
  const [dateFilterPerformer, setDateFilterPerformer] = useState<FilterType[]>([]);
  const [dateFilterTheme, setDateFilterTheme] = useState<FilterType[]>([]);
  const [displayData, setDisplayData] = useState<VideoType[] | any>([]);
  const [displayType, setDisplayType] = useState<number>(2);
  const [editData, setEditData] = useState<VideoType | undefined>(undefined);
  const [modalAction, setModalAction] = useState<ActionType>("Create");
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [selectedThemes, setSelectedThemes] = useState<string[] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: VideoDataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleEditClick = (record: VideoType) => {
    setShowDrawer(true);
    setModalAction("Edit");
    setEditData(record);
  };

  const handleDeleteClick = (videoId: number) => {
    setShowDeleteModal(true);
    setDeleteId(videoId);
  };

  const handleClose = () => {
    form.resetFields();
    setEditData(undefined);
    setShowDrawer(false);
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  }

  const handleDelete = async () => {
    const data = await faceCallVideoDelete(deleteId);

    if (data?.status === "SUCCESS") {
      refreshData();
      handleClose();
      closeDeleteModal();
      message.success(renderAntDMessageConfig("Successful"));
    } else {
      message.error(renderAntDMessageConfig("Failed"));
    }
  }

  const handleSubmit = async (values: any) => {
    let method: MethodType;
    let payload;
    let filePayload;

    const file = values.videoFile[0];

    if (file.originFileObj) {
      const base64File = await fileToBase64(file.originFileObj);
      filePayload = {
        fileName: file.name,
        base64File
      }
    } else {
      filePayload = {
        fileName: file.name
      }
    }

    if (modalAction === "Create") {
      method = "POST";
      payload = {
        songId: values.songId,
        performerId: values.performerId,
        themeId: values.themeId || 0,
        videoFile: filePayload,
        ...(values.publishDate && { startDate: dayjs(values.publishDate).format("YYYY-MM-DD") }),
        ...(values.stopDate && { endDate: dayjs(values.stopDate).format("YYYY-MM-DD") }),
      };
    } else {
      method = "PATCH";
      payload = {
        videoId: values.videoId,
        videoFile: filePayload,
        ...(values.publishDate && { startDate: dayjs(values.publishDate).format("YYYY-MM-DD") }),
        ...(values.stopDate && { endDate: dayjs(values.stopDate).format("YYYY-MM-DD") }),
      };
    }

    const data = await faceCallVideoMutation(method, payload);

    if (data?.status === "SUCCESS") {
      refreshData();
      handleClose();
      message.success(renderAntDMessageConfig("Successful"));
    } else {
      message.error(renderAntDMessageConfig("Failed"));
    }
  };

  const filterOption: any = (
    input: string,
    option: { label: string; value: string }
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  useEffect(() => {
    if (videosData) {
      if (displayType === 2) //InUse
        setDisplayData(videosData.filter(x => x.inUse));
      else if (displayType === 3) //Upcoming
        setDisplayData(videosData.filter(x => x.isUpcoming));
      else if (displayType === 4) //Theme
        setDisplayData(videosData.filter(x => x.themeId !== 0));
      else if (displayType === 5) //NotInUse
        setDisplayData(videosData.filter(x => !x.inUse));

      const uniquePerformer = [
        ...new Set<string>(videosData.map((video: VideoType) => video.performerName)),
      ].sort((a, b) => a.localeCompare(b));

      const filterFormPerformer = uniquePerformer.map((item) => ({
        text: item,
        value: item,
      }));

      setDateFilterPerformer(filterFormPerformer);
    }

    if (videoOptions) {
      const filterFormTheme = videoOptions.themes
        .filter(x => x.themeId !== 0)
        .map((item) => ({
          text: item.themeName,
          value: item.themeId.toString(),
        })) || [];

      setDateFilterTheme(filterFormTheme);
    }
  }, [displayType, videosData, videoOptions]);

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        videoId: editData.videoId,
        songId: editData.songId,
        performerId: editData.performerId,
        themeId: editData.themeId,
        publishDate: editData.startDate ? dayjs(editData.startDate) : "",
        stopDate: editData.endDate ? dayjs(editData.endDate) : "",
        ...(editData.videoFileName ? {
          videoFile: [
            {
              uid: "0",
              name: editData.videoFileName,
              status: "done",
              url: editData.videoFileUrl
            },
          ]
        } : {}),
      });
    }
  }, [editData, form]);

  return (
    <>
      <div className="table-container">
        <div className="table-menu-container">
          <Radio.Group
            optionType="button"
            options={faceCallVideosQueryTypes.map((type) => ({
              label: type.typeName,
              value: type.id,
            }))}
            defaultValue={2}
            onChange={(e) => setDisplayType(e.target.value)}
          />
          {isAdmin &&
            <Button
              type="primary"
              style={{ marginLeft: 8 }}
              icon={<PlusOutlined />}
              onClick={() => {
                setModalAction("Create");
                setShowDrawer(true);
                setEditData(undefined);
              }}
            >
              Create New
            </Button>
          }
        </div>
        <Table
          rowKey={(record: VideoType) => record.videoId}
          onChange={(pagination, filters) => {
            setSelectPerformers(filters.performerName as string[]);
            setSelectedThemes(filters.themeId as string[]);
          }}
          columns={createFaceCallVideosColumns(
            dateFilterPerformer,
            dateFilterTheme,
            displayType,
            searchInput,
            searchText,
            searchedColumn,
            selectPerformers,
            selectedThemes,
            isAdmin,
            handleSearch,
            handleReset,
            handleEditClick,
            handleDeleteClick
          )}
          dataSource={displayData}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            pageSizeOptions: ["10", "20", "30", "40"],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) =>
              `Total ${total} items`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </div>
      <div>
        <Drawer
          title={modalAction === "Create" ? "Create new video" : "Edit the video"}
          open={showDrawer}
          onClose={() => handleClose()}
          width={500}
          className="faceCallDrawer"
        >
          <Form
            name="theme-form"
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
          >
            <Form.Item
              name="videoId"
              hidden
            >
              <Input />
            </Form.Item>
            {modalAction === "Create" ?
              < Form.Item
                className="form-item"
                label="Song title"
                name="songId"
                rules={[requiredRule]}
              >
                <Select
                  showSearch
                  placeholder="- Select -"
                  optionFilterProp="children"
                  filterOption={filterOption}
                  options={videoOptions?.songs?.map((item: any) => ({
                    label: item.songTitle,
                    value: item.songId,
                  }))} />
              </Form.Item>
              :
              <Form.Item label="Song title" name="title">
                <p style={{ color: Colors.notEditableText }}>
                  {editData?.songTitle}
                </p>
              </Form.Item>
            }

            {modalAction === "Create" ?
              <Form.Item
                className="form-item"
                label="Performer"
                name="performerId"
                rules={[requiredRule]}
              >
                <Select
                  showSearch
                  placeholder="- Select -"
                  optionFilterProp="children"
                  filterOption={filterOption}
                  optionLabelProp="label"
                >
                  {videoOptions?.performers.map((performer) => (
                    <Select.Option
                      key={performer.performerId}
                      value={performer.performerId}
                      label={performer.performerName}
                    >
                      <div
                        style={{
                          display: "flex",
                          height: 94,
                          alignItems: "center",
                        }}
                      >
                        <Image
                          src={performer.photoFileUrl}
                          style={{ width: 84, height: "auto", marginRight: 8 }}
                        />
                        <p>{performer.performerName}</p>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              :
              <Form.Item label="Performer" name="title">
                <p style={{ color: Colors.notEditableText }}>
                  {editData?.performerName}
                </p>
              </Form.Item>
            }

            {modalAction === "Create" ?
              <Form.Item
                className="form-item"
                label="Theme"
                name="themeId"
                extra="Unpublish the song if the theme has expired"
              >
                <Select options={videoOptions?.themes?.map((item: any) => ({
                  label: item.themeName,
                  value: item.themeId,
                }))} />
              </Form.Item>
              :
              <Form.Item label="Theme" name="title">
                <p style={{ color: Colors.notEditableText }}>
                  {videoOptions?.themes.find(item => item.themeId === editData?.themeId)?.themeName ?? editData?.themeId}
                </p>
              </Form.Item>
            }

            <Form.Item
              label="Video file"
              name="videoFile"
              className="form-item"
              valuePropName="fileList"
              rules={[requiredRule]}
              getValueFromEvent={(e) => e.fileList}
            >
              <Upload
                {...createUploadProps("mp4", "videoFile", 1)}
              >
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Publish date"
              name="publishDate"
            >
              <DatePicker
                picker="month"
                format="YYYY-MM-01"
                className="month-picker"
              />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Stop date"
              name="stopDate"
            >
              <DatePicker
                format="YYYY-MM-DD"
                disabledDate={(current) => {
                  const publishDate = form.getFieldValue("publishDate");
                  if (!publishDate) return false;

                  return current && current < dayjs(publishDate).startOf("month");
                }}
              />
            </Form.Item>
            <DrawerFooter
              handleCancel={() => {
                form.resetFields();
                setEditData(undefined);
                setShowDrawer(false);
              }}
              handleDelete={modalAction === "Edit"
                ? () => handleDeleteClick(editData?.videoId!)
                : undefined}
            />
          </Form>
        </Drawer>
      </div >
      <GeneralModal
        title={"Are you sure you want to delete the item?"}
        text={""}
        open={showDeleteModal}
        close={closeDeleteModal}
        handleDelete={() => handleDelete()}
        isConfirm={false}
        isDeletion={true}
        width={417}
      />
    </>
  );
};

export default VideosTabContent;
